import './why-did-you-render';

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import {
    Route,
    RouterProvider,
    createBrowserRouter,
    createRoutesFromElements,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
} from 'react-router-dom';
import axios from 'axios';
import { LicenseManager } from 'ag-grid-enterprise';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';
import { queryClient } from './services/queryClient';
import { Auth0ProviderWithHistory } from './components/Auth/Auth0ProviderWithHistory';
import App from './App';
import ErrorFallback from './pages/ErrorFallback';
import { sentryDsn, isDevelopment, envName, apiURL, agGridLicense } from './config';
import './i18n';

Sentry.init({
    dsn: sentryDsn,
    integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring.
    // Session Replay
    replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    enabled: !isDevelopment,
    environment: envName,
});
LicenseManager.setLicenseKey(agGridLicense);

axios.defaults.baseURL = apiURL;

// A DATA router (createBrowserRouter), not <BrowserRouter> — CALL-783 needs `useBlocker`, which throws
// outside one, to stop an in-app navigation from silently discarding an unsaved scenario draft. There is
// no lighter way round it: the sidebar navigates programmatically (SideDrawer calls navigate()), so
// intercepting link clicks would miss the entire main menu.
//
// The route tree is unchanged — one catch-all whose element still wraps everything, so App keeps owning
// the real routing and Auth0ProviderWithHistory keeps calling useNavigate inside a router.
// wrapCreateBrowserRouterV7 is Sentry's supported replacement for withSentryReactRouterV7Routing under
// the data-router API; reactRouterV7BrowserTracingIntegration above stays as configured.
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter(
    createRoutesFromElements(
        <Route
            path="*"
            element={
                <Sentry.ErrorBoundary fallback={ErrorFallback}>
                    <Auth0ProviderWithHistory>
                        <App />
                    </Auth0ProviderWithHistory>
                </Sentry.ErrorBoundary>
            }
        />
    )
);

ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    </React.StrictMode>
);
